/**
 * @file useXmppUnreadCounts.js
 *
 * Background hook that connects a secondary XMPP client (resource "web-bg")
 * on dashboard load, queries MAM for each DM room since the user's
 * last_read_at, and stores unread counts in the dm Redux slice.
 *
 * The client joins all DM rooms (with no history) so live messages are
 * delivered for real-time increment, and issues MAM queries for the initial
 * unread count on session start.
 */

import { useEffect, useRef } from 'react'
import { createClient } from 'stanza'
import { useQuery } from '@tanstack/react-query'
import { useDispatch } from '@/store'
import { xmppConfig } from '@/config'
import { XMPP_BASE_CREDENTIALS, XMPP_DM_LIST, XMPP_DM_MARK_SEEN } from '@/endpoints'
import axiosInstance from '@/lib/axios'
import { incrementRoomUnread, setRoomUnread } from '@/slices/dm'
import useAuth from '@/hooks/useAuth'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fetchBaseCredentials = () =>
    axiosInstance.get(XMPP_BASE_CREDENTIALS).then((r) => r.data)

const fetchDmRooms = () =>
    axiosInstance.get(XMPP_DM_LIST).then((r) => r.data)

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Mounts a background XMPP client for unread count detection.
 * Call once in DashboardLayout — produces no JSX.
 */
export function useXmppUnreadCounts() {
    const dispatch = useDispatch()
    const { activeOrg } = useAuth()
    const activeOrgId = activeOrg?.id

    // Ref keeps rooms current without being a useEffect dependency.
    // This prevents the XMPP client from being torn down and rebuilt
    // every time React Query refreshes lastReadAt timestamps.
    const roomsRef = useRef([])

    const { data: creds } = useQuery({
        queryKey: ['xmpp-base-credentials'],
        queryFn: fetchBaseCredentials,
        staleTime: 10 * 60 * 1000,
        retry: false
    })

    const { data: rooms } = useQuery({
        queryKey: ['xmpp-dm-list', activeOrgId],
        queryFn: fetchDmRooms,
        staleTime: 30 * 1000,
        retry: false,
        enabled: Boolean(activeOrgId)
    })

    // Always keep the ref up to date on every render (not a dependency).
    roomsRef.current = rooms ?? []

    // Stable key that only changes when rooms are added or removed —
    // NOT when lastReadAt timestamps update. The XMPP effect only
    // reconnects for real structural changes to the room list.
    const roomJidsKey = rooms?.map((r) => r.roomJid).sort().join('\n') ?? ''

    useEffect(() => {
        if (!creds || !roomJidsKey || !xmppConfig.wsUrl || !xmppConfig.service) return

        let mounted = true
        let client = null

        const fullJid = creds.jid.includes('/') ? creds.jid : `${creds.jid}/web-bg`

        client = createClient({
            jid: fullJid,
            password: creds.password,
            server: xmppConfig.service,
            transports: { websocket: xmppConfig.wsUrl },
            autoReconnect: true,
            maxReconnectBackoff: 60
        })

        client.on('session:started', async () => {
            if (!mounted) return

            // Read rooms from ref so we always use the latest lastReadAt
            // values (updated by ConnectedDMRoom mark-seen calls) without
            // the effect needing rooms as a dependency.
            const currentRooms = roomsRef.current

            // Build a Set of known DM room JIDs for fast lookup
            const dmRoomJids = new Set(currentRooms.map((r) => r.roomJid.toLowerCase()))

            // Step 1: MAM query each room to populate initial unread counts.
            //
            // For each room we resolve the effective "last read" timestamp as
            // the MORE RECENT of:
            //   a) localStorage  – written by ConnectedDMRoom on every open/close;
            //                      survives page refresh, immune to backend lag.
            //   b) backend       – server-persisted lastReadAt for cross-device sync.
            //
            // Rooms where neither source has a timestamp have never been tracked
            // — we initialise them to "now" and skip the MAM query so we don't
            // surface the full message history as unread.
            for (const room of currentRooms) {
                if (!mounted) return

                const localTs = localStorage.getItem(`dm_read_${room.roomJid}`)
                const backendTs = room.lastReadAt

                // Pick the most recent timestamp from either source.
                let effectiveLastReadAt
                if (localTs && backendTs) {
                    effectiveLastReadAt =
                        new Date(localTs) > new Date(backendTs) ? localTs : backendTs
                } else {
                    effectiveLastReadAt = localTs || backendTs
                }

                if (!effectiveLastReadAt) {
                    // First time this room is tracked — initialise to now so
                    // future sessions start clean without surfacing full history.
                    const now = new Date().toISOString()
                    localStorage.setItem(`dm_read_${room.roomJid}`, now)
                    axiosInstance.post(XMPP_DM_MARK_SEEN(room.otherUser.id)).catch((_err) => {
                        /* best-effort */
                    })
                    continue
                }

                try {
                    const result = await client.searchHistory({
                        to: room.roomJid,
                        start: new Date(effectiveLastReadAt),
                        paging: { max: 50 }
                    })
                    if (!mounted) return
                    // Use results.length (actual messages returned) rather than
                    // result.paging.count which some ejabberd versions report as
                    // the total archive size instead of the filtered count.
                    const count = result.results?.length ?? 0
                    // Skip if the user has this room open right now
                    const isOpen = window.location.pathname.endsWith(`/${room.otherUser.id}`)
                    if (!isOpen) {
                        dispatch(setRoomUnread({ roomJid: room.roomJid, count }))
                    }
                } catch (_err) {
                    // MAM query failed for this room — skip silently
                }
            }

            // Step 2: Join all rooms with no history so live messages are delivered
            for (const room of currentRooms) {
                if (!mounted) return
                try {
                    await client.joinRoom(room.roomJid, creds.nick, {
                        history: { maxStanzas: 0 }
                    })
                } catch (_err) {
                    // join failed — real-time updates won't work for this room
                }
            }

            // Store the JID set for groupchat filtering after session start
            client._dmRoomJids = dmRoomJids
        })

        // Real-time increment for messages received while online.
        // Skip delayed/historical messages (MUC history on join, offline delivery)
        // — those are already counted by the MAM query above.
        client.on('groupchat', (msg) => {
            if (!mounted) return
            if (!msg.body) return
            if (msg.delay) return
            const bareFrom = (msg.from ?? '').split('/')[0].toLowerCase()

            // Use the set built during session:started; fall back to ref if not yet set
            const jidSet = client._dmRoomJids ??
                new Set(roomsRef.current.map((r) => r.roomJid.toLowerCase()))
            if (!jidSet.has(bareFrom)) return

            // Skip messages sent by this user (nick matches)
            const senderNick = (msg.from ?? '').split('/')[1]
            if (senderNick === creds.nick) return

            const originalRoom = roomsRef.current.find(
                (r) => r.roomJid.toLowerCase() === bareFrom
            )
            if (!originalRoom) return

            // Skip if the user currently has this room open
            if (window.location.pathname.endsWith(`/${originalRoom.otherUser.id}`)) return

            dispatch(incrementRoomUnread(originalRoom.roomJid))
        })

        client.on('auth:failed', () => {
            if (client) client.config.autoReconnect = false
        })

        client.connect()

        return () => {
            mounted = false
            if (client) {
                client.config.autoReconnect = false
                for (const room of roomsRef.current) {
                    client.leaveRoom(room.roomJid, creds.nick).catch((_err) => {
                        /* best-effort */
                    })
                }
                client.disconnect().catch((_err) => {
                    /* best-effort */
                })
            }
        }
        // Re-run only when credentials change or rooms are added/removed.
        // lastReadAt updates (from mark-seen calls) do NOT trigger reconnect.
    }, [creds, roomJidsKey, dispatch])
}
